import { Col, Space } from "components/primitives";
import { Palette } from "constants/widgets";
import { ColorPalette } from "material-ui-color";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";

interface InfoState {
  noMargin: boolean;
  label: string;
  options: OptionState[];
  graph: GraphState;
  point: GraphState;
  removeButtonLabel: string;
  resetButtonLabel: string;
}

interface OptionState {
  label: string;
  type: string;
}

interface GraphState {
  label: string;
  switchLabel: string;
  range: any;
}

export const CartesianPlaneSetting: WidgetSettingFC<any, InfoState> = (props) => {
  const {
    data,
    setData,
    info: { noMargin = false, graph, point, removeButtonLabel, resetButtonLabel },
    state,
  } = props;

  const range = data.range;

  const [ranges, setRanges] = useState({
    leftRange: range.leftRange,
    rightRange: range.rightRange,
    bottomRange: range.bottomRange,
    topRange: range.topRange,
    xStep: range.xStep,
    yStep: range.yStep,
    gridSpacingX: range.gridSpacingX,
    gridSpacingY: range.gridSpacingY,
  });
  const [text, setText] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [coordinate, setCoordinate] = useState<any>({ xValue: 0, yValue: 0 });

  // useEffect(() => {
  //   // Set the point x and y when the point is moved
  //   if (state.grid.range.xValue && state.grid.range.yValue)
  //     setCoordinate({
  //       xValue: state.grid.range.xValue,
  //       yValue: state.grid.range.yValue,
  //     });
  // }, [state.grid.range.yValue, state.grid.range.xValue]);

  useEffect(() => {
    setIsChecked(data.disableDrag);
  }, [data.disableDrag]);

  useEffect(() => {
    // Fill the input label of selected point and line
    if (state.grid.type === "point") {
      const text = state.grid.point[state.grid.pointIndex]?.label ?? "";
      setText(text);
    }

    if (state.grid.type === "line") {
      const text = state.grid.line[state.grid.lineIndex]?.start?.label ?? "";
      setText(text);
    }
  }, [state]);

  const onChangeOptions = (value: number | string, type: string) => {
    setRanges({ ...ranges, [type]: value });
  };

  const generateOptions = () => {
    setRanges(ranges);
    setData({ ...data, ...state.grid, range: ranges });
    const selectedPoint = state.grid.point[state.grid.pointIndex];

    if (selectedPoint?.x && selectedPoint?.y) {
      setCoordinate({
        xValue: selectedPoint.x,
        yValue: selectedPoint.y,
      });
    }
  };

  const onColorChange = (color: string) => {
    const body = {
      ...state.grid,
      ...data,
      pointIndex: state.grid.pointIndex,
      lineIndex: state.grid.lineIndex,
      point: state.grid.point,
      line: state.grid.line,
    };

    if (
      state.grid.type === "point" &&
      state.grid.pointIndex !== null &&
      state.grid.pointIndex >= 0
    ) {
      body.point[state.grid.pointIndex].color = color;
    }

    if (state.grid.type === "line" && state.grid.lineIndex !== null && state.grid.lineIndex >= 0) {
      body.line[state.grid.lineIndex].start.color = color;
    }

    setData(body);
  };

  const onChangeLabel = (value: string) => {
    const body = {
      ...state.grid,
      ...data,
      pointIndex: state.grid.pointIndex,
      lineIndex: state.grid.lineIndex,
      point: state.grid.point,
      line: state.grid.line,
    };

    if (
      state.grid.type === "point" &&
      state.grid.pointIndex !== null &&
      state.grid.pointIndex >= 0
    ) {
      body.point[state.grid.pointIndex].label = value;
    }

    if (state.grid.type === "line" && state.grid.lineIndex !== null && state.grid.lineIndex >= 0) {
      body.line[state.grid.lineIndex].start.label = value;
    }

    setData(body);
    setText(value);
  };

  const onChangeSetting = (key: string, key2: string, value: any) => {
    const index = state.grid.pointIndex;
    const updatedPoints = [...data.point];
    updatedPoints[index] = {
      ...state.grid.point[index],
      [key]: value,
    };

    setData({
      ...data,
      ...state.grid,
      pointIndex: state.grid.pointIndex,
      range: { ...data.range, ...coordinate, [key2]: value },
      point: updatedPoints,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setText("");
    }
  };

  return (
    <OuterContainer noMargin={noMargin}>
      <p className="label">Grid</p>
      <Row>
        <OptionButton
          selected={data.gridSize === 10}
          onClick={() => {
            setData({
              ...data,
              ...state.grid,
              gridSize: 10,
              range: {
                ...data.range,
                leftRange: -10,
                rightRange: 10,
                topRange: 10,
                bottomRange: -10,
              },
            });

            setRanges({
              ...range,
              leftRange: -10,
              rightRange: 10,
              bottomRange: -10,
              topRange: 10,
            });
          }}
        >
          10x10
        </OptionButton>
        <OptionButton
          selected={data.gridSize === 20}
          onClick={() => {
            setData({
              ...data,
              ...state.grid,
              gridSize: 20,
              range: {
                ...data.range,
                leftRange: -20,
                rightRange: 20,
                topRange: 20,
                bottomRange: -20,
              },
            });

            setRanges({
              ...range,
              leftRange: -20,
              rightRange: 20,
              bottomRange: -20,
              topRange: 20,
            });
          }}
        >
          20x20
        </OptionButton>
      </Row>
      <Space />
      <p className="label">{graph.label}</p>
      <OptionButton center>x</OptionButton>
      <Space />
      <Row center>
        <Stack gap={1}>
          <Label>min</Label>
          <Input
            type="number"
            value={ranges.leftRange}
            min={-99}
            max={0}
            onChange={(e) => {
              if (e.target.value === "") {
                onChangeOptions("", "leftRange");
              }

              const value = +e.target.value;
              if (value <= -1 && value >= -99) {
                onChangeOptions(value, "leftRange");
              }
            }}
          />
        </Stack>
        <Stack gap={1}>
          <Label>max</Label>
          <Input
            type="number"
            value={ranges.rightRange}
            min={0}
            max={999}
            onChange={(e) => {
              if (e.target.value === "") {
                onChangeOptions("", "rightRange");
              }
              const value = +e.target.value;
              if (value >= 1 && value <= 999) {
                onChangeOptions(value, "rightRange");
              }
            }}
          />
        </Stack>
        <Stack gap={1}>
          <Label>step</Label>
          <Input
            type="number"
            value={ranges.xStep}
            min={1}
            max={10}
            onChange={(e) => {
              if (e.target.value === "") onChangeOptions("", "xStep");

              const value = +e.target.value;
              if (value >= 1 && value <= 10) {
                onChangeOptions(value, "xStep");
              }
            }}
          />
        </Stack>
      </Row>
      <Space gap={16} />
      <OptionButton center>y</OptionButton>
      <Space />
      <Row center>
        <Stack gap={1}>
          <Label>min</Label>
          <Input
            type="number"
            value={ranges.bottomRange}
            min={-99}
            max={0}
            onChange={(e) => {
              if (e.target.value === "") {
                onChangeOptions("", "bottomRange");
              }

              const value = +e.target.value;
              if (value <= -1 && value >= -99) {
                onChangeOptions(value, "bottomRange");
              }
            }}
          />
        </Stack>
        <Stack gap={1}>
          <Label>max</Label>
          <Input
            type="number"
            value={ranges.topRange}
            min={0}
            max={999}
            onChange={(e) => {
              if (e.target.value === "") {
                onChangeOptions("", "topRange");
              }
              const value = +e.target.value;
              if (value >= 1 && value <= 999) {
                onChangeOptions(value, "topRange");
              }
            }}
          />
        </Stack>
        <Stack gap={1}>
          <Label>step</Label>
          <Input
            type="number"
            value={ranges.yStep}
            min={1}
            max={10}
            onChange={(e) => {
              if (e.target.value === "") onChangeOptions("", "yStep");

              const value = +e.target.value;
              if (value >= 1 && value <= 10) {
                onChangeOptions(value, "yStep");
              }
            }}
          />
        </Stack>
      </Row>
      <Space gap={16} />
      <OptionButton center onClick={generateOptions}>
        Generate
      </OptionButton>
      <Space gap={16} />
      {/* <p className="label">{point.label}</p> */}
      {/* <Row>
        <Label>x</Label>
        <Input
          type="number"
          value={coordinate.xValue}
          min={range.leftRange}
          max={range.rightRange}
          onChange={(e) => {
            if (e.target.value === "") {
              onChangeOptions("", "xValue");
              setCoordinate({ ...coordinate, xValue: "" });
              return;
            }

            const value = +e.target.value;
            if (
              value <= Math.abs(range.leftRange) ||
              value <= Math.abs(range.rightRange)
            ) {
              setCoordinate({ ...coordinate, xValue: value });
              onChangeSetting("x", "xValue", value);
            }
          }}
        />
      </Row>
      <Space />
      <Row>
        <Label>y</Label>
        <Input
          type="number"
          value={coordinate.yValue}
          min={range.bottomRange}
          max={range.topRange}
          onChange={(e) => {
            if (e.target.value === "") {
              onChangeOptions("", "yValue");
              setCoordinate({ ...coordinate, yValue: "" });
              return;
            }

            const value = +e.target.value;
            if (
              value <= Math.abs(range.topRange) ||
              value <= Math.abs(range.bottomRange)
            ) {
              setCoordinate({ ...coordinate, yValue: value });
              onChangeSetting("y", "yValue", value);
            }
          }}
        />
      </Row> */}
      {/* <Space gap={16} /> */}
      <p className="label">Label</p>
      <Input
        type="text"
        value={text}
        onChange={(e) => onChangeLabel(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <Space gap={16} />
      <p className="label">Color</p>
      <ColorPalette palette={Palette} onSelect={(c) => onColorChange(Palette[c])} />
      <Space gap={16} />
      {/* <OptionButton
        center
        onClick={() => {
          if (state.grid.type === "point" && state.grid.pointIndex !== null) {
            state.grid.point.splice(state.grid.pointIndex, 1);
          }

          if (state.grid.type === "line" && state.grid.lineIndex !== null) {
            state.grid.line.splice(state.grid.lineIndex, 1);
          }

          setData(state.grid);
        }}
      >
        {removeButtonLabel}
      </OptionButton> */}
      <Space gap={16} />
      <OptionButton
        center
        onClick={() => {
          setData({
            point: [],
            line: [],
            range: {
              leftRange: -10,
              rightRange: 10,
              bottomRange: -10,
              topRange: 10,
              xStep: 1,
              yStep: 1,
              xValue: 0,
              yValue: 0,
              gridSpacingX: 25,
              gridSpacingY: 25,
            },
            disableDrag: true,
            pointIndex: null,
            lineIndex: null,
            type: null,
            gridSize: 10,
          });

          setRanges({
            leftRange: -10,
            rightRange: 10,
            bottomRange: -10,
            topRange: 10,
            xStep: 1,
            yStep: 1,
            gridSpacingX: 25,
            gridSpacingY: 25,
          });

          setCoordinate({
            xValue: 0,
            yValue: 0,
          });
          setText("");
        }}
      >
        {resetButtonLabel}
      </OptionButton>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)<{ noMargin: boolean }>`
  margin-bottom: ${(props) => (props.noMargin ? "0px" : "35px")};

  .label {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 12pt;
  }
`;

const OptionButton = styled.button<{ selected?: boolean; center?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.center ? "center" : "flex-start")};
  gap: 8px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  border: 2px solid ${(props) => (props.selected ? "#007BFF" : "#ccc")};
  background-color: ${(props) => (props.selected ? "#e7f1ff" : "#f0f0f0")};
  color: ${(props) => (props.selected ? "#007BFF" : "#333")};
  border-radius: 5px;

  &:hover {
    border-color: #007bff;
    background-color: #e7f1ff;
  }
`;

const Row = styled.div<{ center?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => (props.center ? `space-evenly` : "flex-start")};
  gap: 8px;
`;

const Stack = styled.div<{
  gap?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${(props) => (props.gap ? `${props.gap}px` : "8px")};
`;

const Dot = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid #d0d0d0;
  border-radius: 10px;
  background-color: #d0d0d0;
`;

const Line = styled.div`
  width: 20px;
  height: 5px;
  background-color: #e0e0e0;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #333;
`;

const Input = styled.input`
  padding: 0.4rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 26px;

  :before {
    position: absolute;
    content: "";
    height: 80%;
    aspect-ratio: 1;
    left: 0;
    top: 50%;
    transform: translate(15%, -50%);
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const InnerContainer = styled(Row)`
  align-items: center;
  gap: 10px;

  span {
    color: var(--gray);
  }
`;

const Switch = styled(Form.Label)`
  position: relative;
  display: inline-block;
  height: 30px;
  margin: 0;
  aspect-ratio: 2;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:checked + ${Slider} {
    background-color: var(--primary);
  }

  input:checked + ${Slider}:before {
    left: 100%;
    transform: translate(-115%, -50%);
  }
`;
